import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import { LayoutGrid, Settings, History, LogOut, Wifi, WifiOff, RefreshCw, Eye, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/marketing/SiteNav";
import { useAuth } from "@/context/AuthContext";
import { flushQueue, queue } from "@/lib/api";
import { Pick, Loading } from "./bits";
import { NoOrg } from "./NoOrg";

const NAV_ALL = [["/app", "Portfolio", LayoutGrid, true], ["/app/audit", "Audit trail", History], ["/app/manual", "Manual", BookOpen], ["/app/settings", "Settings", Settings]];

function useSync() {
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(queue.list().length);
  const sync = async () => {
    if (!queue.list().length) return;
    const r = await flushQueue();
    if (r.synced) toast.success(`Synced ${r.synced} offline change${r.synced > 1 ? "s" : ""}`);
    r.failed.forEach((f) => toast.error(f));
  };
  useEffect(() => {
    const up = () => { setOnline(true); sync(); };
    const down = () => setOnline(false);
    const q = () => setPending(queue.list().length);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    window.addEventListener("pc-queue", q);
    if (navigator.onLine) sync();
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); window.removeEventListener("pc-queue", q); };
  }, []);
  return { online, pending, sync };
}

const SyncBadge = ({ online, pending, sync }) => (
  <button onClick={sync} data-testid="sync-status" className={`flex items-center gap-1.5 font-mono text-[11px] px-2 py-1 border ${online ? "border-emerald-500/40 text-emerald-400" : "border-red-500/40 text-red-400"}`}>
    {online ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}{online ? "ONLINE" : "OFFLINE"}
    {pending > 0 && <span className="flex items-center gap-1 text-amber-400"><RefreshCw className="w-3 h-3" />{pending}</span>}
  </button>
);

function OrgSwitch() {
  const { me, switchOrg } = useAuth();
  if (me.orgs.length < 2) return <p className="text-sm text-slate-200 font-medium truncate">{me.orgs[0]?.name}</p>;
  return <Pick testid="org-switcher" value={me.active_org_id} onChange={(v) => switchOrg(v).then(() => toast.success("Organisation switched"))} options={me.orgs.map((o) => [o.id, o.name])} />;
}

export function AppShell() {
  const { me, logout } = useAuth();
  const location = useLocation();
  const sync = useSync();
  if (me === undefined && !location.state?.user) return <div className="min-h-screen bg-[#080D1A]"><Loading /></div>;
  if (!me) return <Navigate to="/login" replace />;
  if (!me.orgs?.length) return <NoOrg />;
  const NAV = NAV_ALL.filter(([to]) => !(me.is_demo && to === "/app/manual"));

  return (
    <div className="min-h-screen bg-[#080D1A]" data-testid="app-shell">
      <aside className="hidden md:flex flex-col w-64 fixed inset-y-0 left-0 bg-[#0A1124] border-r border-[#1E2D4A] z-40">
        <div className="h-16 flex items-center px-5 border-b border-[#1E2D4A]"><Logo small /></div>
        <div className="p-4 border-b border-[#1E2D4A] space-y-2">
          <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Organisation</p>
          <OrgSwitch />
          <p className="font-mono text-[10px] text-amber-500 uppercase" data-testid="current-role">{me.role}</p>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map(([to, label, Icon, end]) => (
            <NavLink key={to} to={to} end={end} data-testid={`sidenav-${label.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-amber-500/10 text-amber-400 border-l-2 border-amber-500" : "text-slate-300 hover:bg-[#131C31]"}`}>
              <Icon className="w-4 h-4" />{label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[#1E2D4A] space-y-3">
          <SyncBadge {...sync} />
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0"><p className="text-sm text-slate-200 truncate">{me.name || me.email}</p><p className="text-xs text-slate-500 truncate">{me.email}</p></div>
            <button onClick={logout} data-testid="logout-btn" className="p-2 text-slate-400 hover:text-white" aria-label="Sign out"><LogOut className="w-4 h-4" /></button>
          </div>
        </div>
      </aside>
      <header className="md:hidden sticky top-0 z-40 h-14 bg-[#0F172A]/95 backdrop-blur border-b border-[#1E2D4A] flex items-center justify-between px-4">
        <Link to="/app" className="font-heading font-bold text-slate-100">PULSE</Link>
        <div className="flex items-center gap-2"><SyncBadge {...sync} /><button onClick={logout} className="p-2 text-slate-400" data-testid="logout-btn-mobile" aria-label="Sign out"><LogOut className="w-4 h-4" /></button></div>
      </header>
      <main className="md:ml-64 px-4 py-5 md:px-8 md:py-8 pb-24 md:pb-8">
        {me.is_demo && (
          <div className="mb-5 flex items-center gap-2 text-xs text-amber-300 border border-amber-500/30 bg-amber-500/5 px-3 py-2" data-testid="demo-banner">
            <Eye className="w-4 h-4" />Read-only demo with fictional data. <a href="/#demo" className="underline ml-1">Request a demo</a> to try it on your project.
          </div>
        )}
        <div className="md:hidden mb-4"><OrgSwitch /></div>
        <Outlet />
      </main>
      <nav className="md:hidden fixed bottom-0 inset-x-0 h-16 bg-[#0F172A] border-t border-[#1E2D4A] flex justify-around items-center z-50" data-testid="mobile-bottom-nav">
        {NAV.map(([to, label, Icon, end]) => (
          <NavLink key={to} to={to} end={end} className={({ isActive }) => `flex flex-col items-center gap-1 text-[11px] px-3 py-1 ${isActive ? "text-amber-400" : "text-slate-400"}`}>
            <Icon className="w-5 h-5" />{label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
